import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";
import { toast } from "react-toastify";
import "../../style/Dropdown.css";
import "./UsersList.css";
import api from "../../api/axiosInstance";

import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Pagination,
  Card,
  CardContent,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import AppDropdown from "../../components/AppDropdown";

const PermissionSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    className={`permission-switch ${checked ? "is-on" : "is-off"}`}
    role="switch"
    aria-checked={checked}
    aria-label="Toggle permission"
    disabled={disabled}
    onClick={() => onChange(!checked)}
  >
    <span className="permission-switch-track">
      <span className="permission-switch-thumb" />
    </span>
  </button>
);

const AnimatedRefreshIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="15" 
    height="15" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={{ display: 'block' }}
  >
    <g>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
      <animateTransform
        attributeName="transform"
        attributeType="XML"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="1s"
        repeatCount="indefinite"
        fill="freeze" />
    </g>
  </svg>
);

const UsersList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, userId: null });
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "EMPLOYEE",
    active: true,
    canManageCustomer: false,
    canCreateCustomer: false,
    canManageGoodsItem: false,
    canManageChallan: false,
    canManageProfile: false,
    canManageSetting: false,
    canManagePassbook: false,
    canAccessCRM: false
  });

  const [errors, setErrors] = useState({});

  // FETCH USERS
  const fetchUsers = useCallback(async () => {
    setRefreshing(true);
    setLoading(true);

    try {
      const response = await api.get('/settings/users/getAllUsers');
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  // DELETE USER
  const deleteUser = async (id) => {
    setConfirmModal({ isOpen: true, userId: id });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/settings/users/delete/${confirmModal.userId}`);

      fetchUsers();
      setConfirmModal({ isOpen: false, userId: null });
      toast.success("User deleted successfully!");

    } catch (error) {
      console.error("Delete failed:", error);
      setConfirmModal({ isOpen: false, userId: null });
      toast.error("Failed to delete user");
    }
  };

  const cancelDelete = () => {
    setConfirmModal({ isOpen: false, userId: null });
  };

  // TOGGLE STATUS
  const toggleStatus = async (username, active) => {
    try {
      await api.put(`/settings/users/${username}/status`, null, {
        params: { active: !active }
      });

      fetchUsers();
      toast.success(`User ${!active ? 'activated' : 'deactivated'} successfully!`);

    } catch (error) {
      console.error("Status update failed:", error);
      toast.error("Failed to update user status");
    }
  };

  // Reset form data helper
  const resetFormData = useCallback(() => {
    setFormData({
      username: "",
      password: "",
      role: "EMPLOYEE",
      active: true,
      canManageCustomer: false,
      canCreateCustomer: false,
      canManageGoodsItem: false,
      canManageChallan: false,
      canManageProfile: false,
      canManageSetting: false,
      canManagePassbook: false,
      canAccessCRM: false
    });
    setErrors({});
  }, []);

  // ADD USER
  const handleAddUser = () => {
    resetFormData();
    setAddUserDialog(true);
  };

  const handleCloseAddDialog = () => {
    setAddUserDialog(false);
    // Navigate back to base users route if we're on the /new route
    if (location.pathname === '/dashboard/users/new') {
      navigate('/dashboard/users');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Permission switch handler
  // MUI Switch gives the new checked state as the second onChange argument.
  // This keeps each permission independent and makes ON/OFF reliable.
  const handlePermissionChange = (permission, checked) => {
    setFormData(prev => ({
      ...prev,
      [permission]: Boolean(checked),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Include all permission fields in the request
        const submitData = {
          ...formData,
          canManageCustomer: formData.canManageCustomer ?? false,
          canManagePassbook: formData.canManagePassbook ?? false,
          canAccessCRM: formData.canAccessCRM ?? false
        };
        
        const response = await api.post('/settings/users/create', submitData);

        console.log("User created successfully:", response.data);
        toast.success("User created successfully!");
        setAddUserDialog(false);
        fetchUsers();
        // Navigate back to base users route if we're on the /new route
        if (location.pathname === '/dashboard/users/new') {
          navigate('/dashboard/users');
        }

      } catch (error) {
        console.error("Error creating user:", error);
        toast.error(error.response?.data?.message || error.message || "Failed to create user");
      }
    }
  };

  // SEARCH FILTER
  const filteredUsers = users.filter((user) =>
    user.username?.toLowerCase().includes(search.toLowerCase()) ||
    user.role?.toLowerCase().includes(search.toLowerCase())
  );

  // Client-side pagination
  const paginatedUsers = filteredUsers.slice(page * size, (page + 1) * size);
  const calculatedTotalPages = Math.ceil(filteredUsers.length / size) || 0;
  const calculatedTotalElements = filteredUsers.length;

  const handlePageChange = useCallback((event, value) => {
    setPage(value - 1);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const quickSearch = location.state?.quickSearch;
    if (!quickSearch) return;

    setSearch(String(quickSearch));
    setPage(0);
  }, [location.state]);

  // Reset page when search or size changes
  useEffect(() => {
    setPage(0);
  }, [search, size]);

  // Open add user dialog if route is /dashboard/users/new
  useEffect(() => {
    if (location.pathname === '/dashboard/users/new') {
      resetFormData();
      setAddUserDialog(true);
    }
  }, [location.pathname, resetFormData]);

  return (
    <div className="usersListContainer">
      <div className="usersListContent">
        <Box
          sx={{
            p: "8px 4px 36px",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >

      {/* Custom Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete User"
        cancelText="Cancel"
      />

      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: "#8b5cf6",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 8px 20px rgba(139,92,246,.18)"
            }}
          >
            <PeopleIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              className="users-page-title"
              sx={{
                fontSize: "38px",
                fontWeight: 700,
                color: "#0F172A",
                lineHeight: 1.1
              }}
            >
              Users
            </Typography>
            <Typography
              className="users-page-subtitle"
              sx={{
                mt: 0.5,
                color: "#64748B",
                fontSize: 15
              }}
            >
              Manage user accounts, roles, and permissions
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={fetchUsers}
            disabled={refreshing}
            startIcon={refreshing ? <AnimatedRefreshIcon /> : <RefreshIcon />}
            sx={{
              height: 48,
              borderRadius: "10px",
              fontWeight: 600,
              px: 2.5,
              textTransform: "none",
              transition: "all .25s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(37,99,235,.15)",
              },
              "&:active": {
                transform: "scale(.98)",
              },
            }}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            sx={{
              height: 48,
              borderRadius: "10px",
              fontWeight: 600,
              padding: "0 20px",
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                boxShadow: '0 6px 8px -1px rgba(139, 92, 246, 0.4)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      {!filtersOpen ? (
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setFiltersOpen(true)}
          sx={{
            mb: 3,
            height: 48,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            borderColor: "#E5E7EB",
            color: "#64748b",
            "&:hover": {
              borderColor: "#2563eb",
              background: "#eff6ff",
              color: "#2563eb",
            },
          }}
        >
          Show Filters
        </Button>
      ) : (
        <Card
          className="users-filters-card"
          sx={{
            mb: 3,
            borderRadius: "22px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 10px 30px rgba(15,23,42,.05)",
            overflow: "visible",
            background: "#fff"
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              "&:last-child": {
                pb: { xs: 2, sm: 2.5, md: 3 }
              }
            }}
          >
            {/* Header row */}
            <Box sx={{
              display: "flex",
              alignItems: { xs: "center", sm: "flex-start" },
              justifyContent: "space-between",
              mb: { xs: 2, sm: 2.5 },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }} onClick={() => setFiltersOpen(false)}>
                <Box sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#eff6ff",
                  color: "#2563eb",
                }}>
                  <SearchIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                  Filters
                </Typography>
                <ExpandLessIcon sx={{ fontSize: 20, color: "#64748b" }} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setSearch("")}
                  disabled={!search}
                  sx={{
                    height: 36,
                    mt: { xs: 0, sm: 0.5 },
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    textTransform: "none",
                    color: "#64748b",
                    "&:hover": {
                      background: "#f1f5f9",
                      color: "#334155",
                    },
                    "&:disabled": {
                      color: "#cbd5e1",
                      background: "transparent",
                      "&:hover": {
                        background: "transparent",
                        color: "#cbd5e1",
                      }
                    }
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>

            {/* Fields row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "1fr",
              },
              alignItems: "flex-end",
              gap: { xs: 2, sm: 2, md: 2 }
            }}>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 64 }} key="search-input-container-v2">
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#64748B", mb: 0.5 }}>
                  Search
                </Typography>
                <Box className="users-search-wrapper" sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "400px",
                  height: 38,
                  borderRadius: "10px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  transition: "all .25s ease",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#CBD5E1",
                  },
                  "&:focus-within": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#2563EB",
                    borderWidth: "2px",
                  }
                }}>
                  <Box className="users-search-icon" sx={{ position: "absolute", left: 14, zIndex: 1, pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                  </Box>
                  <input
                    className="users-search-input"
                    type="text"
                    placeholder="Search by username or role..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "0 14px 0 38px",
                      border: "none",
                      outline: "none",
                      fontSize: "13px",
                      color: "#0F172A",
                      backgroundColor: "transparent",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Box>

            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Box className="users-table-card" sx={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px',
        boxSizing: 'border-box',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <TableContainer
          className="users-table-container"
          sx={{
            minHeight: 500,
            overflowX: 'auto',
            maxWidth: '100%',
            "@media (min-width: 1024px)": {
              overflowX: 'hidden',
            },
            "@media (max-width: 1023px)": {
              overflowX: 'auto',
            },
            "&::-webkit-scrollbar": {
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 20,
              background: "#cbd5e1",
            },
          }}
        >
            <Table sx={{
              minWidth: { xs: 700, md: '100%' },
              width: '100%',
              tableLayout: { md: 'auto', xs: 'fixed' }
            }}>
              <TableHead sx={{
                '& th': { position: 'sticky', top: 0, zIndex: 5, background: 'white' }
              }}>
                <TableRow>
                  <TableCell align="center" sx={{ width: 80, fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', padding: { xs: '12px 10px', md: '16px' } }}>
                    Sr. No.
                  </TableCell>
                  <TableCell align="center" sx={{ width: 200, fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', padding: { xs: '12px 10px', md: '16px' } }}>
                    Username
                  </TableCell>
                  <TableCell align="center" sx={{ width: 150, fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', padding: { xs: '12px 10px', md: '16px' } }}>
                    Role
                  </TableCell>
                  <TableCell align="center" sx={{ width: 150, fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', padding: { xs: '12px 10px', md: '16px' } }}>
                    Status
                  </TableCell>
                  <TableCell align="center" sx={{ width: 90, minWidth: 90, maxWidth: 90, px: 1, py: 1.5, fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.6px', textAlign: 'center', verticalAlign: 'middle' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`} sx={{ height: 64 }}>
                      <TableCell align="center" sx={{ width: 80 }}>
                        <Skeleton variant="text" width={30} height={24} />
                      </TableCell>
                      <TableCell align="center" sx={{ width: 200 }}>
                        <Skeleton variant="text" width={120} height={24} />
                      </TableCell>
                      <TableCell align="center" sx={{ width: 150 }}>
                        <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '999px' }} />
                      </TableCell>
                      <TableCell align="center" sx={{ width: 150 }}>
                        <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '999px' }} />
                      </TableCell>
                      <TableCell align="center" sx={{ width: 90, px: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                          <Skeleton variant="circular" width={30} height={30} />
                          <Skeleton variant="circular" width={30} height={30} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography color="#94a3b8">No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user, index) => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: '#F8FBFF',
                          transform: 'translateX(2px)'
                        },
                        height: 64,
                        '& td': { padding: { xs: '12px 10px', md: '16px' }, verticalAlign: 'middle' },
                        transition: 'background-color .2s ease, transform .2s ease'
                      }}
                    >
                      <TableCell align="center" sx={{ color: '#475569' }}>{page * size + index + 1}</TableCell>
                    <TableCell align="center" sx={{ color: '#1e293b', fontWeight: 500 }}>{user.username}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.role}
                        sx={{
                          padding: { xs: '4px 10px', md: '6px 14px' },
                          borderRadius: '999px',
                          fontSize: { xs: '11px', md: '12px' },
                          fontWeight: 700,
                          letterSpacing: '.3px',
                          height: 'auto',
                          background: '#e2e8f0',
                          color: '#475569',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.active ? 'Active' : 'Inactive'}
                        sx={{
                          padding: { xs: '4px 10px', md: '6px 14px' },
                          borderRadius: '999px',
                          fontSize: { xs: '11px', md: '12px' },
                          fontWeight: 700,
                          letterSpacing: '.3px',
                          height: 'auto',
                          cursor: 'pointer',
                          ...(user.active && { background: '#dcfce7', color: '#166534' }),
                          ...(!user.active && { background: '#fee2e2', color: '#991b1b' }),
                        }}
                        onClick={() => toggleStatus(user.username, user.active)}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ width: 90, minWidth: 90, maxWidth: 90, px: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', width: '100%' }}>
                        <IconButton
                          onClick={() => navigate(`/dashboard/edit-user/${user.id}`)}
                          sx={{
                            color: '#8b5cf6',
                            width: 30,
                            height: 30,
                            padding: 0,
                            borderRadius: '10px',
                            '&:hover': { background: '#F8FAFC', transform: 'translateY(-2px)' }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          onClick={() => deleteUser(user.id)}
                          sx={{
                            color: '#ef4444',
                            width: 30,
                            height: 30,
                            padding: 0,
                            borderRadius: '10px',
                            '&:hover': { background: '#F8FAFC', transform: 'translateY(-2px)' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

          {/* Pagination */}
          {calculatedTotalPages > 0 && (<Box
              className="users-pagination"
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", md: "flex-start" }, gap: 1 }}>
                {calculatedTotalElements > 0 && (
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    Showing {page * size + 1}–{Math.min((page + 1) * size, calculatedTotalElements)} of {calculatedTotalElements} records
                  </Typography>
                )}
                <Pagination
                count={calculatedTotalPages}
                page={page + 1}
                onChange={handlePageChange}
                shape="rounded"
                variant="outlined"
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    transition: "transform .2s, box-shadow .2s, background-color .2s",
                    borderRadius: "10px",
                    fontWeight: 600,
                    minWidth: 38,
                    height: 38,
                  },
                  "& .MuiPaginationItem-root:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 10px rgba(0,0,0,.15)",
                  },
                  "& .MuiPaginationItem-root:active": {
                    transform: "scale(.95)",
                  },
                  "& .Mui-selected": {
                    transform: "scale(1.05)",
                    boxShadow: "0 6px 14px rgba(37,99,235,.25)",
                  },
                  "& ul": {
                    justifyContent: {
                      xs: "center",
                      md: "flex-start",
                    },
                  },
                }}
              />
              </Box>
              <Box
                sx={{
                  width: {
                    xs: 150,
                    sm: 130,
                  },
                  transition: "all .2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500, color: "#475569", mb: 0.75 }}>Rows per page</Typography>
                <AppDropdown
                  value={size}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' }
                  ]}
                  onChange={(newSize) => {
                    setSize(newSize);
                    setPage(0);
                  }}
                />
              </Box>
            </Box>
          )}
      </Box>

      {/* Add User Dialog */}
      <Dialog
        className="users-add-user-dialog"
        open={addUserDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{
          className: "users-dialog-paper",
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#1e293b', fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' }, pb: 2, px: { xs: 2, sm: 3 } }}>Add New User</DialogTitle>
        <DialogContent sx={{ pb: 3, px: { xs: 2, sm: 3 } }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username}
              sx={{ mb: 2.5 }}
              size="medium"
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              sx={{ mb: 2.5 }}
              size="medium"
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: '#8b5cf6' }}>
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </IconButton>
                ),
              }}
            />

            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569', mb: 1 }}>
                Role
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  displayEmpty
                  sx={{
                    height: 42,
                    '& .MuiSelect-select': {
                      py: 1.5,
                    }
                  }}
                >
                  <MenuItem value="" disabled>Select role</MenuItem>
                  <MenuItem value="EMPLOYEE">Employee</MenuItem>
                  <MenuItem value="ADMIN">Admin</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#1e293b', fontWeight: 600 }}>
              Permissions
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 0,
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Manage Customer
                </Typography>
                <PermissionSwitch
                  checked={formData.canManageCustomer}
                  onChange={(checked) => handlePermissionChange("canManageCustomer", checked)}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Create Customer
                </Typography>
                <PermissionSwitch
                  checked={formData.canCreateCustomer}
                  onChange={(checked) => handlePermissionChange("canCreateCustomer", checked)}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Manage Goods
                </Typography>
                <PermissionSwitch
                  checked={formData.canManageGoodsItem}
                  onChange={(checked) => handlePermissionChange("canManageGoodsItem", checked)}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Manage Challan
                </Typography>
                <PermissionSwitch
                  checked={formData.canManageChallan}
                  onChange={(checked) => handlePermissionChange("canManageChallan", checked)}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Manage Profile
                </Typography>
                <PermissionSwitch
                  checked={formData.canManageProfile}
                  onChange={(checked) => handlePermissionChange("canManageProfile", checked)}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Manage Settings
                </Typography>
                <PermissionSwitch
                  checked={formData.canManageSetting}
                  onChange={(checked) => handlePermissionChange("canManageSetting", checked)}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Manage Passbook
                </Typography>
                <PermissionSwitch
                  checked={formData.canManagePassbook}
                  onChange={(checked) => handlePermissionChange("canManagePassbook", checked)}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                '&:last-child': { borderBottom: 'none' },
                position: 'relative',
                zIndex: 1
              }}>
                <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>
                  Can Access CRM
                </Typography>
                <PermissionSwitch
                  checked={formData.canAccessCRM}
                  onChange={(checked) => handlePermissionChange("canAccessCRM", checked)}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
          <Button 
            onClick={handleCloseAddDialog} 
            sx={{ 
              color: '#64748b', 
              fontWeight: 600,
              px: 3,
              height: 42,
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ 
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
              boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.3)',
              px: 3,
              height: 42,
              borderRadius: 2,
              fontWeight: 600,
              '&:hover': { 
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', 
                boxShadow: '0 6px 8px -1px rgba(139, 92, 246, 0.4)',
                transform: 'translateY(-1px)' 
              }, 
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' 
            }}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </div>
    </div>
  );
};

export default UsersList;